// src/modules/app-views/account/pages/Subscription/modals/CscTokenModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { KeyOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import cscTokenStep from './Step1';

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Validate CSC token format
 * @param {string} token - The token to validate
 * @returns {{ valid: boolean, message?: string }}
 */
const validateCscToken = token => {
  if (!token || typeof token !== 'string') {
    return { valid: false, message: translateX('csc_token_required') };
  }

  const trimmed = token.trim();
  const length = trimmed.length;

  // Common lengths: 32-36 for UUID, up to 40 in some states
  if (length < 16 || length > 40) {
    return { valid: false, message: translateX('csc_token_invalid_length') };
  }

  // Allow alphanumeric + hyphens only (most common formats)
  if (!/^[0-9a-fA-F-]+$/.test(trimmed)) {
    return { valid: false, message: translateX('csc_token_invalid_chars') };
  }

  return { valid: true };
};

/**
 * Mask a CSC token for display (shows only last 4 characters)
 * @param {string} token - The plain token
 * @returns {string} Masked token like "••••••••-••••-XXXX"
 */
const maskCscToken = token => {
  if (!token || token.length < 4) return '••••••••';
  const lastFour = token.slice(-4);
  const maskedLength = token.length - 4;
  const masked = '•'.repeat(Math.min(maskedLength, 32));
  return `${masked}${lastFour}`;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CscTokenModal = ({ show, setShow, afterClose, onSuccess, uf }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, goFocus }) => {
    const token = form.getFieldValue('cscToken');

    // Validate token format
    const validation = validateCscToken(token);
    if (!validation.valid) {
      await appService.message({ flag: 'w', text: validation.message });
      // Focus on the token field and return false to keep modal open
      goFocus?.('cscToken', true);
      return false;
    }

    // Create masked version
    const tokenMasked = maskCscToken(token.trim());

    // Call success callback with both values
    if (onSuccess) {
      await onSuccess({
        token: token.trim(),
        tokenMasked,
      });
    }

    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(() => [cscTokenStep()], []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<KeyOutlined />}
      title="csc_token_title"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={480}
      maskClosable={false}
      initialValues={{ _uf: uf }}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CscTokenModal;
export { maskCscToken, validateCscToken };
